import { debug } from 'debug';
import { useState } from 'react';
import { createEventSourceHook } from '~/lib/create-event-source-hook';
import { userEventOrBaseSchema, type UserEventOrBase } from '~/lib/user-event.schema';
import { useAuthStore } from '~/store';

interface UseUserEventsStreamProps {
  userId: string | null | undefined;
  onEvent?: (event: UserEventOrBase) => void;
  enabled?: boolean;
}

const logger = debug('app:user-events-stream');

const useUserEventsEventSource = createEventSourceHook<UserEventOrBase>({
  parseMessage: (data: string) => {
    try {
      const parsedData = JSON.parse(data) as unknown;
      return userEventOrBaseSchema.parse(parsedData);
    } catch {
      return null;
    }
  },
});

export const useUserEventsStream = ({ userId, enabled = true, onEvent }: UseUserEventsStreamProps) => {
  const [events, setEvents] = useState<UserEventOrBase[]>([]);
  const [latestEvent, setLatestEvent] = useState<UserEventOrBase | null>(null);

  const { isConnected, error, disconnect } = useUserEventsEventSource({
    pathname: `/stream/users/${userId}/events`,
    enabled: enabled && Boolean(userId),
    getAccessToken: () => useAuthStore.getState().accessToken,
    onMessage: (data) => {
      logger('User event received:', data.type, data.data.action, data);
      setLatestEvent(data);
      setEvents((previous) => [...previous, data]);
      onEvent?.(data);
    },
  });

  return {
    events,
    latestEvent,
    isConnected,
    error,
    disconnect,
  };
};
